import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';
import { 
  setAccessToken, 
  getAccessToken, 
  setRefreshToken, 
  getRefreshToken, 
  setUser as setUserStorage, 
  getUser, 
  clearTokens 
} from '../utils/tokenStorage';
import { migrateFromLocalStorage } from '../utils/migrateStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Migrate from old localStorage to sessionStorage (one-time migration)
    migrateFromLocalStorage();
    
    const token = getAccessToken();
    const userData = getUser();
    
    if (token && userData) {
      setUser(userData);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    setLoading(false);
  }, []);

  const login = async (email, password, mfaCode = null) => {
    try {
      const response = await api.post('/auth/login', { email, password, mfaCode });
      const { accessToken, refreshToken, user: userData } = response.data;
      
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      setUserStorage(userData);
      
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const signup = async (email, password, name, role) => {
    try {
      // For super_admin, use the admin-signup endpoint (doesn't need role parameter)
      if (role === 'super_admin' || role === 'main_admin') {
        const response = await api.post('/auth/admin-signup', { email, password, name });
        const { accessToken, refreshToken, user: userData } = response.data;
        
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);
        setUserStorage(userData);
        
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        setUser(userData);
        
        return { success: true };
      } else {
        // For other roles, use regular signup endpoint (if it exists)
        const response = await api.post('/auth/signup', { email, password, name, role });
        const { accessToken, refreshToken, user: userData } = response.data;
        
        setAccessToken(accessToken);
        setRefreshToken(refreshToken);
        setUserStorage(userData);
        
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        setUser(userData);
        
        return { success: true };
      }
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Signup failed' 
      };
    }
  };

  const logout = () => {
    clearTokens();
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = getRefreshToken();
      if (!refreshToken) throw new Error('No refresh token');
      
      const response = await api.post('/auth/refresh', { refreshToken });
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      
      setAccessToken(accessToken);
      setRefreshToken(newRefreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      return true;
    } catch (error) {
      logout();
      return false;
    }
  };

  const refreshUserData = async () => {
    try {
      // Get current user data from backend
      const response = await api.get('/auth/me');
      if (response.data && response.data.user) {
        const userData = response.data.user;
        setUserStorage(userData); // Update storage
        setUser(userData); // Update state
        return true;
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
    return false;
  };

  const value = {
    user,
    login,
    signup,
    logout,
    refreshAccessToken,
    refreshUserData,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
